import {
    createContext,
    FC,
    ReactNode,
    useContext,
    useMemo,
    useState,
} from 'react';
import {useLocale as setLanguage} from 'ttag';
import {Locale} from 'types';
import getPreferredLanguage from 'utils/locale';

import {LocaleReturnType} from './types';
import {initializeTtag} from './utils';

initializeTtag();

const CONTEXT_NOT_INITIALIZED_ERROR = new Error(
    'LocaleContext not initialized, must be used within a LocaleProvider',
);

const LocaleContext = createContext<LocaleReturnType>({
    locale: getPreferredLanguage(),
    setLocale: () => {
        throw CONTEXT_NOT_INITIALIZED_ERROR;
    },
});

export type LocaleProviderSettings = {
    children?: ReactNode;
    overrideLocale?: Locale;
};
const LocaleProvider: FC<LocaleProviderSettings> = ({
    children,
    overrideLocale,
}) => {
    const [locale, setLocale] = useState<Locale>(getPreferredLanguage());

    const handleSetLocale = (locale: Locale): void => {
        setLocale(locale);
    };

    const value: LocaleReturnType = useMemo(() => {
        const newLocale = overrideLocale || locale;
        setLanguage(newLocale);
        return {
            locale: newLocale,
            setLocale: handleSetLocale,
        };
    }, [locale, overrideLocale]);

    return (
        <LocaleContext.Provider key={locale} value={value}>
            {children}
        </LocaleContext.Provider>
    );
};
export default LocaleProvider;

export const useLocale = (): Locale => {
    const context = useContext(LocaleContext);
    return context.locale;
};

export const useSetLocale = (): ((locale: Locale) => void) => {
    const context = useContext(LocaleContext);
    return context.setLocale;
};
